import React, { useEffect, useReducer, useState } from 'react'
import { lockedReducer } from '../../../utils/defaultReducer'
import {
    Dialog, Box, DialogActions, DialogContent, DialogTitle, Button, TextField,
    MenuItem, FormControl, InputLabel, Select,
    Tooltip, Autocomplete
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingButton from '@mui/lab/LoadingButton';
import { AutomationType, ContactType, EditType, StatusOptions } from './types';
import api from '../../../api';
import { getSession } from '../../../auth';
import { getStandardFieldSettings } from '../standard-fields';

export const PERIODS = {
    '': '',
    'MINUTES': { label: 'Minuto(s)', max: 60 * 24 * 7 },
    'HOURS': { label: 'Hora(s)', max: 24 * 7 },
    'DAYS': { label: 'Dia(s)', max: 30 * 6 },
    'WORKDAYS': { label: 'Dia útil', max: 30 * 6 },
    'WEEKS': { label: 'Semana(s)', max: 24 }
}

export const TRIGGER_FIELD_OPERATORS = {
    '': '',
    'EQUALS': 'Igual',
    'NOT_EQUALS': 'Diferente',
    'CONTAINS': 'Contém',
    'NOT_CONTAINS': 'Não contém',
    'GREATER_THAN': 'Maior',
    'LESS_THAN': 'Menor',
}

export const DEFAULT_TRIGGER_FIELD_OPTIONS = [
    { value: 'status', label: 'Status' },
    { value: 'title', label: 'Título' },
    { value: 'description', label: 'Descrição' },
    { value: 'value', label: 'Valor' },
    { value: 'owner', label: 'Vendedor' },
    { value: 'contact_enterprise', label: 'Empresa' },
    { value: 'contact', label: 'Contato' },
]

export const getTriggerFieldOptions = (opportunityCustomFields = [], journey = null) => {
    const standardFieldSettings = getStandardFieldSettings(journey)

    return [
        ...DEFAULT_TRIGGER_FIELD_OPTIONS.map(field => {
            const customLabel = String(standardFieldSettings?.[field.value]?.label || '').trim()

            return {
                ...field,
                label: customLabel || field.label,
            }
        }),
        ...(opportunityCustomFields || []).map(field => ({
            value: `custom_field_${field.id}`,
            label: `Campo customizado: ${field.name}`,
            field_type: field.field_type,
            options: field.options || [],
        }))
    ]
}

export const getConditions = (triggerData) => {
    const conditions = triggerData?.conditions
    if (Array.isArray(conditions) && conditions.length > 0)
        return conditions
    if (triggerData?.field || triggerData?.operator || (triggerData?.value !== undefined && triggerData?.value !== null && triggerData?.value !== ''))
        return [{ field: triggerData.field || '', operator: triggerData.operator || '', value: triggerData.value ?? '' }]
    return []
}

const getOptionName = (option) => typeof option === 'string' ? option : (option?.name || option?.contact_enterprise_name || '')

const TRIGGER_VALUE_ASYNC_FIELDS = ['contact', 'contact_enterprise']

const EMPTY_CONDITION = { field: '', operator: '', value: '' }

const isConditionStarted = (condition) => Boolean(
    condition?.field ||
    condition?.operator ||
    (condition?.value !== undefined && condition?.value !== null && condition?.value !== '')
)

const isConditionComplete = (condition) => Boolean(
    condition?.field &&
    condition?.operator &&
    condition?.value !== undefined && condition?.value !== null && condition?.value !== ''
)

const defaultState = {
    id: 0,
    title: '',
    trigger: '',
    type: '',
    active: true
}

const defaulTriggerData = {
    type: '',
    time: 1,
}

const defaulTypeData = {
    email: '',
    action: '',
    destination: '',
    bot: '',
    type: '',
    entrypoint: '',
    campaign: '',
    queue: '',
    template: '',
    origin: '',
    subject: '',
    cc: '',
    cco: '',
    emailMessageData: {},
    config: [],
    parameters: [],
    mediaParameters: {},
    sender: '',
    sendSignature: false,
    target_journey_id: "",     // "" = mesma jornada
    copy_options: null,        // ou {} / ou um default
    copy_history: true,
    reactions: {
        replied: { action: 'NONE', destination: null },
        no_reply: { action: 'NONE', destination: null, time: '', type: 'HOURS' },
        failed: { action: 'NONE', destination: null },
    },
    status: ''
}

const validateData = (state, triggerData, typeData) => {
    const response = []

    if (!state.title)
        response.push(<Box>Digite o nome da automação</Box>)
    if (state.trigger === '')
        response.push(<Box>Selecione o gatilho</Box>)
    if (state.trigger === 'TIME_ON_COLUMN'){
        if (!triggerData?.type)
            response.push(<Box>Selecione o período</Box>)
         if (!triggerData?.time)
            response.push(<Box>Selecione o tempo</Box>)
    }

    const conditions = triggerData?.conditions || []
    conditions.forEach((condition, index) => {
        if (!isConditionStarted(condition)) return
        if (!condition.field)
            response.push(<Box>Selecione o campo da condição {index + 1}</Box>)
        if (!condition.operator)
            response.push(<Box>Selecione o filtro da condição {index + 1}</Box>)
        if (condition.value === undefined || condition.value === null || condition.value === '')
            response.push(<Box>Digite o valor de comparação da condição {index + 1}</Box>)
    })

    if (state.type === '')
        response.push(<Box>Selecione o tipo</Box>)
    if (state.type === 'CONTACT'){
        if (!typeData.type)
            response.push(<Box>Selecione o canal</Box>)
        if (typeData.type === 'WHATSAPP'){
            if (!typeData.sender)
                response.push(<Box>Selecione o número de envio</Box>)
            if (!typeData.template)
                response.push(<Box>Selecione o template</Box>)
        } else if (typeData.type === 'EMAIL'){
            if (!typeData.email)
                response.push(<Box>Selecione a caixa de e-mail de origem</Box>)
            if (!typeData.subject)
                response.push(<Box>Digite o assunto</Box>)
        } else if (typeData.type === 'VOICE'){
            if (!typeData.campaign)
            response.push(<Box>Selecione a campanha de voz</Box>)
        }
        if (['WHATSAPP', 'EMAIL'].includes(typeData.type)) {
            const r = typeData.reactions || {}
            const noReply = r.no_reply || {}
            if (noReply.action && noReply.action !== 'NONE') {
                const t = Number(noReply.time)
                if (!t || t < 1) response.push(<Box>Informe o tempo para "Não respondeu" (mín. 1)</Box>)
                if (!noReply.type) response.push(<Box>Selecione a unidade de tempo para "Não respondeu"</Box>)
            }
        }

    }

    if (state.type === 'EDIT'){
        if (['MOVE', 'COPY'].includes(typeData.action) && !typeData.destination) {
            response.push(<Box>Selecione o destino</Box>)
        }
        if (typeData.action === 'COPY') {
            // opcional: garantir que tenha pelo menos 1 opção marcada
            const opts = typeData.copy_options || {}
            const any = Object.values(opts).some(Boolean)
            if (!any) response.push(<Box>Selecione ao menos um campo para copiar</Box>)
        }
        if (typeData.action === 'STATUS' && !typeData.status) {
            response.push(<Box>Selecione o status</Box>)
        }
    }

    if (response.length === 0)
        return ''

    return response

}

const ConditionRow = ({ condition, index, onChange, onRemove, showRemove, triggerFieldOptions, disabled }) => {
    const [triggerValueOptions, setTriggerValueOptions] = useState([])
    const [triggerValueSearch, setTriggerValueSearch] = useState('')
    const [triggerValueLoading, setTriggerValueLoading] = useState(false)

    const selectedTriggerField = triggerFieldOptions.find(option => option.value === condition.field)
    const isAsyncTriggerField = TRIGGER_VALUE_ASYNC_FIELDS.includes(condition.field)

    useEffect(() => {
        if (isAsyncTriggerField)
            setTriggerValueSearch(condition.value || '')
    }, [condition.field])

    useEffect(() => {
        if (!isAsyncTriggerField) {
            setTriggerValueOptions([])
            setTriggerValueSearch('')
            return
        }

        let active = true
        const loadOptions = async () => {
            setTriggerValueLoading(true)
            try {
                const enterpriseId = getSession().profile.enterprise.id
                const endpoint = condition.field === 'contact' ? 'contacts' : 'contact_enterprises'
                const search = triggerValueSearch ? `&search=${encodeURIComponent(triggerValueSearch)}` : ''
                const res = await api.get(`api/enterprises/${enterpriseId}/${endpoint}/simplified/?limit=25&offset=0${search}`)
                if (!active) return
                setTriggerValueOptions(res.data?.results || res.data || [])
            } finally {
                if (active) setTriggerValueLoading(false)
            }
        }

        loadOptions()
        return () => { active = false }
    }, [isAsyncTriggerField, condition.field, triggerValueSearch])

    const renderTriggerValueField = () => {
        if (condition.field === 'status') {
            return (
                <FormControl size='small' fullWidth disabled={disabled || !condition.field}>
                    <InputLabel id={`select-trigger-status-value-${index}`}>Valor</InputLabel>
                    <Select
                        labelId={`select-trigger-status-value-${index}`}
                        label='Valor'
                        value={condition.value}
                        onChange={({ target }) => onChange({ value: target.value })}
                    >
                        {Object.entries(StatusOptions).map(([key, value]) => (
                            <MenuItem key={key} value={key}>{value}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        }

        if (selectedTriggerField?.field_type === 'select') {
            return (
                <FormControl size='small' fullWidth disabled={disabled || !condition.field}>
                    <InputLabel id={`select-trigger-custom-field-value-${index}`}>Valor</InputLabel>
                    <Select
                        labelId={`select-trigger-custom-field-value-${index}`}
                        label='Valor'
                        value={condition.value}
                        onChange={({ target }) => onChange({ value: target.value })}
                    >
                        {(selectedTriggerField.options || []).map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        }

        if (isAsyncTriggerField) {
            const selectedValue = triggerValueOptions.find(option => getOptionName(option) === condition.value) || (condition.value ? { name: condition.value } : null)
            return (
                <Autocomplete
                    size='small'
                    fullWidth
                    disabled={disabled || !condition.field}
                    options={triggerValueOptions}
                    value={selectedValue}
                    loading={triggerValueLoading}
                    inputValue={triggerValueSearch}
                    onInputChange={(_, value, reason) => {
                        if (reason !== 'reset') setTriggerValueSearch(value)
                    }}
                    onChange={(_, value) => {
                        const optionName = getOptionName(value)
                        setTriggerValueSearch(optionName)
                        onChange({ value: optionName })
                    }}
                    getOptionLabel={(option) => getOptionName(option)}
                    isOptionEqualToValue={(option, value) => getOptionName(option) === getOptionName(value)}
                    renderInput={(params) => <TextField {...params} label='Valor' />}
                />
            )
        }

        return (
            <TextField
                disabled={disabled || !condition.field}
                label='Valor'
                size='small'
                type={selectedTriggerField?.field_type === 'number' || condition.field === 'value' ? 'number' : 'text'}
                value={condition.value}
                onChange={({ target }) => onChange({ value: target.value })}
                fullWidth
            />
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <FormControl size='small' fullWidth disabled={disabled}>
                <InputLabel id={`select-trigger-field-${index}`}>Campo</InputLabel>
                <Select
                    labelId={`select-trigger-field-${index}`}
                    label='Campo'
                    value={condition.field}
                    onChange={({ target }) => {
                        if (!target.value) {
                            onChange({ field: '', operator: '', value: '' })
                            return
                        }
                        onChange({ field: target.value, value: '' })
                    }}
                >
                    <MenuItem value=''>Sem condição</MenuItem>
                    {triggerFieldOptions.map(field => (
                        <MenuItem key={field.value} value={field.value}>{field.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box className='journey-time-on-column' sx={{ alignItems: 'center' }}>
                <FormControl size='small' fullWidth disabled={disabled || !condition.field}>
                    <InputLabel id={`select-trigger-operator-${index}`}>Filtro</InputLabel>
                    <Select
                        labelId={`select-trigger-operator-${index}`}
                        label='Filtro'
                        value={condition.operator}
                        onChange={({ target }) => onChange({ operator: target.value })}
                    >
                        {Object.entries(TRIGGER_FIELD_OPERATORS).flatMap(([key, value]) => {
                            if (key === '') return []
                            return (<MenuItem key={key} value={key}>{value}</MenuItem>)
                        })}
                    </Select>
                </FormControl>
                {renderTriggerValueField()}
                {showRemove && !disabled &&
                    <Button sx={{ minWidth: '2rem', padding: 0 }} onClick={onRemove}>
                        <DeleteIcon fontSize='small' />
                    </Button>}
            </Box>
        </Box>
    )
}

const EditJourneyAutomation = ({ column, data, handleClose, submit, triggers, types, opportunityCustomFields = null, journey = null }) => {

    const [state, setState] = useReducer(lockedReducer, {
        ...defaultState,
        ...data
    })

    const [triggerData, setTriggerData] = useReducer(lockedReducer, {
        type: data.trigger_data?.type || defaulTriggerData.type,
        time: data.trigger_data?.time ?? defaulTriggerData.time,
        conditions: getConditions(data.trigger_data),
    })

    const [typeData, setTypeData] = useReducer(lockedReducer, {
        ...defaulTypeData,
        ...data.type_data
    })

    const [botList, setBotList] = useState(null)

    const triggerFieldOptions = getTriggerFieldOptions(opportunityCustomFields, journey)
    const conditions = triggerData.conditions?.length ? triggerData.conditions : [EMPTY_CONDITION]
    const validate = validateData(state, triggerData, typeData)

    const updateCondition = (index, patch) => {
        const nextConditions = conditions.map((condition, i) => i === index ? { ...condition, ...patch } : condition)
        setTriggerData({ conditions: nextConditions })
    }

    const addCondition = () => {
        setTriggerData({ conditions: [...conditions, { ...EMPTY_CONDITION }] })
    }

    const removeCondition = (index) => {
        setTriggerData({ conditions: conditions.filter((_, i) => i !== index) })
    }

    return (
        <>
            <Dialog
                open={true}
                keepMounted
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth={'sm'}
            >
                <DialogTitle id="alert-dialog-slide-title" sx={{ display: "flex", justifyContent: "space-between" }}>
                    <span>
                        {state.copy ? 'Copiar' : state.id === 0 ? 'Adicionar' : 'Alterar'} Automação
                    </span>
                    <Box>
                        <Button sx={{ 'minWidth': '2rem', 'padding': '0' }} onClick={handleClose} >
                            <CloseIcon />
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent >
                    <Box className='journey-automation-content'>
                        <Box className='journey-automation-box-select'>
                            <Button
                                variant={state.active === true ? 'contained' : 'standard'}
                                onClick={() => setState({ active: true })}
                                className='journey-automation-box-select-button'>
                                Ativa
                            </Button>
                            <Button
                                variant={state.active === false ? 'contained' : 'standard'}
                                onClick={() => setState({ active: false })}
                                className='journey-automation-box-select-button'>
                                Inativa
                            </Button>
                        </Box>
                        <TextField
                            size='small'
                            label='Nome'
                            value={state.title}
                            onChange={({ target }) => setState({ title: target.value })}
                            inputProps={{ maxLength: 64 }}
                            helperText={`${(state.title || '').length}/64`}
                            fullWidth
                        />
                        <FormControl size='small' fullWidth disabled={state.id}>
                            <InputLabel id='select-triggers'>Gatilho</InputLabel>
                            <Select
                                labelId='select-triggers'
                                label='Gatilho'
                                value={state.trigger}
                                onChange={({ target }) => setState({ trigger: target.value })}
                            >
                                {Object.entries(triggers).flatMap(([key, value]) => {
                                    if (key === '') return []
                                    return (<MenuItem value={key}>{value}</MenuItem>)
                                }
                                )}
                            </Select>
                        </FormControl>
                        {state.trigger === 'TIME_ON_COLUMN' &&
                            <Box className='journey-time-on-column'>
                                <TextField
                                    disabled={state.id}
                                    type='number'
                                    label='Tempo'
                                    size='small'
                                    value={triggerData.time}
                                    onChange={({ target }) => {
                                        let value = Number(target.value)
                                        if (value >= PERIODS[triggerData.type].max) value = PERIODS[triggerData.type].max;
                                        setTriggerData({ time: value })
                                    }}
                                    inputProps={{
                                        min: 1,
                                        max: PERIODS[triggerData.type].max
                                    }}
                                />

                                <FormControl size='small' fullWidth disabled={state.id}>
                                    <InputLabel id='select-trigger-data'>Período</InputLabel>
                                    <Select
                                        labelId='select-trigger-data'
                                        label='Período'
                                        value={triggerData.type}
                                        onChange={({ target }) => {
                                            setTriggerData({ type: target.value })
                                        }}
                                    >
                                        {Object.entries(PERIODS).flatMap(([key, value]) => {
                                            if (key === '') return []
                                            return (<MenuItem value={key}>{value?.label}</MenuItem>)
                                        }
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                        }
                        {state.trigger !== '' &&
                            <>
                                <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                    Condição por campo da oportunidade (opcional)
                                </Box>
                                {conditions.map((condition, index) => (
                                    <React.Fragment key={index}>
                                        {index > 0 &&
                                            <Box sx={{ fontSize: '0.75rem', fontWeight: 500, opacity: 0.6 }}>E</Box>}
                                        <ConditionRow
                                            condition={condition}
                                            index={index}
                                            onChange={(patch) => updateCondition(index, patch)}
                                            onRemove={() => removeCondition(index)}
                                            showRemove={conditions.length > 1}
                                            triggerFieldOptions={triggerFieldOptions}
                                            disabled={state.id}
                                        />
                                    </React.Fragment>
                                ))}
                                {!state.id &&
                                    <Button
                                        size='small'
                                        onClick={addCondition}
                                        disabled={!conditions[conditions.length - 1]?.field}
                                        sx={{ alignSelf: 'flex-start' }}
                                    >
                                        + Adicionar condição
                                    </Button>}
                            </>
                        }
                        <Box className='journey-automation-box-select'>
                            {Object.entries(types).flatMap(([key, value]) => {
                                if (key === '') return []
                                return (<Button
                                    variant={state.type === key ? 'contained' : 'standard'}
                                    onClick={() => setState({ type: key })}
                                    className='journey-automation-box-select-button'>
                                    {value}
                                </Button>)
                            }
                            )}
                        </Box>
                        {state.type === 'CONTACT' && <ContactType columnId={column?.id} parent={typeData} setParent={setTypeData} opportunityCustomFields={opportunityCustomFields} />}
                        {state.type === 'AUTOMATION' && <AutomationType parent={typeData} setParent={setTypeData} botList={botList} setBotList={setBotList} />}
                        {state.type === 'EDIT' && <EditType columnId={column?.id} parent={typeData} setParent={setTypeData} />}

                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: "10px 20px 10px 10px" }}>
                    <Tooltip title={validate}>
                        <span>
                            <LoadingButton
                                onClick={() => {
                                    const finalTriggerData = {
                                        type: triggerData.type,
                                        time: triggerData.time,
                                        conditions: conditions.filter(isConditionComplete),
                                    }
                                    submit({ ...state, trigger_data: finalTriggerData, type_data: typeData })
                                }}
                                color="primary"
                                disabled={validate}
                            >
                                Salvar
                            </LoadingButton>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default EditJourneyAutomation